import { Navigate } from 'react-router-dom'
import type { ReactNode } from 'react'
import { useAuthContext } from '@/app/providers'
import { ROUTES } from '@/app/config'

interface PermissionGuardProps {
  children: ReactNode
  permissions?: string[]
  roles?: string[]
  requireAll?: boolean
  fallbackPath?: string
}

export function PermissionGuard({
  children,
  permissions = [],
  roles = [],
  requireAll = false,
  fallbackPath = ROUTES.DASHBOARD,
}: PermissionGuardProps) {
  const { user } = useAuthContext()

  if (!user) {
    return <Navigate to={ROUTES.LOGIN} replace />
  }

  const hasPermissions = permissions.length === 0 || checkAccess(user.permissions, permissions, requireAll)
  const hasRoles = roles.length === 0 || checkAccess(user.roles, roles, requireAll)

  if (!hasPermissions || !hasRoles) {
    if (import.meta.env.DEV) {
      console.warn('[PermissionGuard] acceso denegado', {
        userRoles: user.roles,
        requiredRoles: roles,
        userPermissions: user.permissions,
        requiredPermissions: permissions,
      })
    }
    return <Navigate to={fallbackPath} replace />
  }

  return <>{children}</>
}

function normalize(value: string): string {
  return value.toLowerCase()
}

function checkAccess(userAccess: string[], required: string[], requireAll: boolean): boolean {
  const normalizedUser = userAccess.map(normalize)
  if (requireAll) {
    return required.every((item) => normalizedUser.includes(normalize(item)))
  }
  return required.some((item) => normalizedUser.includes(normalize(item)))
}
